import { Component, Inject, OnInit, AfterViewInit, OnDestroy, Renderer2, ElementRef, ViewChild, ViewChildren, QueryList, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ProductTplService } from '@app/sales/product-tpl/productTpl.service';
import { ValuesService } from '@app/common/values/values.service';
import * as constants from '@app/shared/constants';
import { C_value } from '@app/shared/_models/common';
import { forkJoin, Observable } from 'rxjs';

import { take, startWith, map } from 'rxjs/operators';

import { ProductPackagingVal, Product, ProductTpl } from '@app/shared/_models/product';

@Component({
  selector: 'app-pick-product-dialog',
  standalone: true,
  styleUrls: ['./selectPackagingQty.component.scss'],
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './selectPackagingQty.component.html',
})
export class SelectPackagingQty implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('qtyInput') qtyInputElt!: ElementRef<HTMLInputElement>;
  @ViewChild('ppvList') ppvListElt?: ElementRef<HTMLElement>;
  @ViewChildren('ppvRow') ppvRowElts?: QueryList<ElementRef<HTMLElement>>;

  packaging: C_value[] = [];
  productPackagingVals: ProductPackagingVal[] = [];
  product!: Product;
  is_ht: boolean = false;
  private removeKeyListener?: () => void;

  public formgroup = this.fb.group({
    // max : une douchette lue fenêtre ouverte tape l'EAN dans la quantité, et son Entrée validerait 3 760 178 860 277 articles.
    qty: this.fb.control<number>(1, [Validators.required, Validators.min(1), Validators.max(9999)]),
    // required : sans conditionnement il n'y a ni prix ni ligne de commande possible. Un produit qui n'en a
    // aucun laissait « Ajouter » actif et renvoyait ppv=null — la ligne partait au total de 0,00 €.
    ppv: this.fb.control<ProductPackagingVal | null>(null, [Validators.required]),
  });

  public getTotalPriceFromformgroupChange$: Observable<any> = this.formgroup.valueChanges.pipe(
    startWith(this.formgroup.value), // emit the current value when subscribed
    map(() => this.formgroup.getRawValue()), // <-- inclut les disabled
    map(val => {
      return {
        total_taxi: Math.round((val.ppv?.lnk_product?.price_taxi || 0) * (val.qty || 0) * (val.ppv?.qtybaseuom || 1) * 100) / 100,
        total_taxx: Math.round((val.ppv?.lnk_product?.price_taxx || 0) * (val.qty || 0) * (val.ppv?.qtybaseuom || 1) * 100) / 100,
      };
    }),
  );

  constructor(
    private renderer: Renderer2,
    private valuesService: ValuesService,
    public dialogRef: MatDialogRef<SelectPackagingQty>,
    private productTplService: ProductTplService,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: Product,
  ) {}

  ngOnInit(): void {
    this.product = this.data;

    let forkJoinSubstriction = forkJoin({
      productPackagingVals: this.productTplService.getPackagingValsArrayByProductId(this.product.id).pipe(take(1)),
      packaging: this.valuesService.getValues$(constants.C_VALUES_UOM).pipe(take(1)),
    }).subscribe(({ productPackagingVals, packaging }) => {
      this.packaging = packaging;
      this.productPackagingVals = productPackagingVals.map(ppv => {
        if (ppv.lnk_product?.lnk_template && ppv.lnk_product.lnk_template.baseuom) {
          (ppv.lnk_product.lnk_template as ProductTpl).lnk_C_value_packaging = packaging.find((pack: C_value) => pack.id === ppv.lnk_product!.lnk_template!.baseuom);
        } // here we put inside lnk_template the c_value_packaging corresponding of the default packaging of this template
        ppv.lnk_C_value_packaging = packaging.find((pack: C_value) => pack.id === ppv.packaging);
        return ppv;
      });

      // Présélection APRÈS l'enrichissement (total et libellé lisent lnk_C_value_packaging) ; à défaut de ppv par défaut,
      // le premier, c'est-à-dire le plus petit (le serveur trie is_default DESC, qtybaseuom ASC).
      const defaut = this.productPackagingVals.find(ppv => ppv.is_default && ppv.id !== undefined) ?? this.productPackagingVals.find(ppv => ppv.id !== undefined);
      if (defaut) {
        this.formgroup.controls['ppv'].setValue(defaut);
      } // un seul choix n'est plus DÉSACTIVÉ (liste visible, plus de select) : la valeur est posée, `required` ne bloque donc que le cas « aucun conditionnement »
      forkJoinSubstriction.unsubscribe(); // one time only
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      // this delay ensures the view is fully and we are after the stack of angular initialization
      const qtyInput = this.qtyInputElt?.nativeElement;
      qtyInput.focus();
      qtyInput.select?.();

      const azertyShiftMap: Record<string, string> = { '&': '1', é: '2', '"': '3', "'": '4', '(': '5', '-': '6', è: '7', _: '8', ç: '9', à: '0' };

      this.removeKeyListener = this.renderer.listen('document', 'keydown', (event: KeyboardEvent) => {
        // Installer le listener quand les ViewChild sont prêts after a little delay setTimeout
        const active = document.activeElement;
        if (event.key === 'Tab') {
          // on gère l’ordre de tab custom
          event.preventDefault();
          if (active === qtyInput && this.productPackagingVals.length > 1) {
            this.ppvListElt?.nativeElement.focus();
          } else {
            qtyInput?.focus();
            qtyInput?.select?.();
          }
        } else if (azertyShiftMap[event.key]) {
          // si on a appuyé sur une touche du haut du clavier azerty on le transforme en chiffre
          event.preventDefault(); // on gère l’ordre de tab custom
          this.formgroup.controls['qty'].setValue(Number((this.formgroup.controls['qty'].getRawValue()?.toString() ?? '') + azertyShiftMap[event.key])); // on ajoute le chiffre à la fin
        } else if (event.key === 'Backspace' || event.key === 'Delete' || Number(event.key).toString() === event.key || /^F\d+$/.test(event.key)) {
          // la regex est pour les touches de type F1 F2 F3 etc reload easy
          // we do nothing the default behavior is ok for us
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
          // La liste est TOUJOURS dépliée : plus de panneau à ouvrir, les quatre flèches déplacent
          // directement la sélection (la droite reste le geste d'avant pour « aller au conditionnement »).
          event.stopPropagation();
          event.preventDefault();
          this.moveSelection(1);
        } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
          event.stopPropagation();
          event.preventDefault();
          this.moveSelection(-1);
        } else if ((event.key === 'Enter' || event.key === 'NumpadEnter') && this.formgroup.valid) {
          // on valide le formulaire uniquement si il est valide
          event.stopPropagation();
          event.preventDefault();
          this.submit();
        } else {
          event.preventDefault();
        } // on bloque toute autre touche
      }); // fin listener
    }, 20); // fin setTimeout  utilisé pour être sûr d'être après le cycle d'init d'angular
  }

  // ─── Le produit, pour l'en-tête ────────────────────────────────────────────────────────────────

  /** Le produit porté par les conditionnements est plus complet que celui passé en data (il vient du
   *  serveur avec son modèle et ses prix) ; il fait foi dès qu'il est chargé. */
  private get loadedProduct(): Product | undefined {
    return this.productPackagingVals[0]?.lnk_product ?? undefined;
  }

  public get productName(): string {
    return this.product?.name || this.loadedProduct?.name || 'Sélection du produit';
  }

  /** La fiche du produit, pour le lien du titre (ouvert dans un nouvel onglet par le gabarit). */
  public get productHref(): string {
    return `/sales/product/${this.product?.id}`;
  }

  /** Le modèle n'est dit que s'il APPORTE quelque chose : sur un produit à plat, il porte le même nom
   *  que le produit et la ligne ne ferait que répéter le titre. */
  public get templateName(): string | undefined {
    const name = this.loadedProduct?.lnk_template?.name ?? this.product?.lnk_template?.name;
    return name && name !== this.productName ? name : undefined;
  }

  /** L'unité de base du modèle (« Paquet ») : c'est l'unité dans laquelle est exprimé le prix. */
  public get baseUnitName(): string | undefined {
    const baseuom = this.loadedProduct?.lnk_template?.baseuom ?? this.product?.lnk_template?.baseuom;
    return this.packaging.find(p => p.id === baseuom)?.name;
  }

  // ─── Les conditionnements ──────────────────────────────────────────────────────────────────────

  public isSelected(ppv: ProductPackagingVal): boolean {
    return this.formgroup.controls['ppv'].value === ppv;
  }

  public selectPpv(ppv: ProductPackagingVal): void {
    this.formgroup.controls['ppv'].setValue(ppv);
  }

  /** Flèches : la sélection tourne sur la liste (les objets sont ceux du tableau, l'identité suffit). */
  private moveSelection(delta: number): void {
    const list = this.productPackagingVals;
    if (list.length < 2) return;
    const cur = list.indexOf(this.formgroup.controls['ppv'].value as ProductPackagingVal);
    const next = cur < 0 ? 0 : (cur + delta + list.length) % list.length;
    this.formgroup.controls['ppv'].setValue(list[next]);
    this.ppvRowElts?.get(next)?.nativeElement.scrollIntoView({ block: 'nearest' });
  }

  /** « Unité », « Carton de 4 Paquets » — même règle que QuotationgeneralComponent.getPackagingName. */
  public packagingLabel(ppv: ProductPackagingVal): string {
    const name = this.packaging.find(p => p.id === ppv.packaging)?.name ?? '';
    const baseuom = ppv.lnk_product?.lnk_template?.baseuom;
    const baseName = this.packaging.find(p => p.id === baseuom)?.name;
    if (name && (ppv.qtybaseuom ?? 1) > 1 && baseuom && baseuom !== ppv.packaging && baseName) {
      return `${name} de ${ppv.qtybaseuom} ${baseName}s`;
    }
    return name || 'Conditionnement';
  }

  /** Ce que contient le conditionnement, dit dans l'unité de base : « 4 paquets », « 1 unité ». */
  public baseQtyLabel(ppv: ProductPackagingVal): string {
    const qty = ppv.qtybaseuom ?? 1;
    const unit = (this.baseUnitName || 'unité').toLowerCase();
    return `${qty} ${unit}${qty > 1 ? 's' : ''}`;
  }

  /** is_sellable / is_purchasable sont NOT NULL en base : les quatre cas sont dits en clair. */
  public usageLabel(ppv: ProductPackagingVal): string {
    if (ppv.is_sellable && ppv.is_purchasable) return 'Vente et achat';
    if (ppv.is_sellable) return 'Vente seule';
    if (ppv.is_purchasable) return 'Achat seul';
    return 'Ni vente ni achat';
  }

  /** Prix public de l'unité de base (celui que porte le produit) et prix du conditionnement entier. */
  public unitPrice(ppv: ProductPackagingVal): number {
    return this.is_ht ? (ppv.lnk_product?.price_taxx ?? 0) : (ppv.lnk_product?.price_taxi ?? 0);
  }

  public packagingPrice(ppv: ProductPackagingVal): number {
    return Math.round(this.unitPrice(ppv) * (ppv.qtybaseuom ?? 1) * 100) / 100;
  }

  /** Toute la donnée du conditionnement, au survol : ce que la ligne ne montre pas y est. */
  public ppvTooltip(ppv: ProductPackagingVal): string {
    return [
      `Conditionnement n°${ppv.id}`,
      `Code-barres : ${ppv.barcode || '—'}`,
      `Quantité en unité de base : ${ppv.qtybaseuom ?? 1}`,
      `Quantité pour le prix unitaire : ${ppv.qtyunitpriceuom ?? '—'}`,
      `Par défaut : ${ppv.is_default ? 'oui' : 'non'}`,
      `Vendable : ${ppv.is_sellable ? 'oui' : 'non'} — Achetable : ${ppv.is_purchasable ? 'oui' : 'non'}`,
    ].join('\n');
  }

  /** Deux conditionnements de même libellé (deux « Unité » : deux EAN du même produit) : seul le
   *  code-barres les sépare, et il faut le dire — c'est la question que pose l'écran. */
  public get hasDuplicateLabels(): boolean {
    const labels = this.productPackagingVals.map(ppv => this.packagingLabel(ppv));
    return new Set(labels).size !== labels.length;
  }

  submit(): void {
    this.dialogRef.close(this.formgroup.getRawValue()); // we close the dialog and return the selected values
  }

  ngOnDestroy() {
    // Nettoyage pour éviter les fuites mémoire
    if (this.removeKeyListener) {
      this.removeKeyListener();
      this.removeKeyListener = undefined; // optionnel, pour éviter de l’appeler deux fois
    }
  }
}
